import { buildFloawchUrl } from '../../utils/buildFloawchUrl';
import { initLpTracking, trackLp, applyNoIndex } from '../../utils/lpTracking';
import { canSendEmailCapture, markEmailCaptureSent } from '../GoedeVoornemens/emailCaptureGuard';
import { hasSentLead, markLeadSent } from '../../utils/leadGuard';
import { suggestEmailFix } from '../../utils/emailTypo';

import { useState, useEffect, useRef } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';

import './Page.css';

const isDev = () =>
  window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok');

const trackEvent = (webhook, payload) => {
  if (isDev()) return;
  fetch(webhook, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(() => {});
};

// ---- Live app-data (floawch) --------------------------------------------
// Publieke, anonieme aggregaten uit de echte app: trainingen gelogd, door Stef
// nagekeken sessies, totaal getild, progressie-mediaan, een echte klant-grafiek
// en recente PR's (alleen initialen - geen namen).
const STATS_URL = 'https://hl-project.nl/api/public/landing-stats';

// Fallback = echte cijfers van 10 juli 2026, zodat de secties nooit leeg staan
// als de API even onbereikbaar is. Wordt bij laden overschreven door de live fetch.
const FALLBACK_STATS = {
  totals: { sessionsLogged: 5766, sessionsReviewed: 4662, totalSets: 97932, totalVolumeKg: 44419477 },
  lastReviewedAt: '2026-07-09T13:32:23.000Z',
  progression: { medianPct: 20, shareImprovedPct: 75, sampleSize: 1161 },
  showcase: {
    exercise: 'Bench press',
    series: [
      { d: '2026-05-04', w: 70 }, { d: '2026-05-11', w: 80 }, { d: '2026-05-18', w: 80 },
      { d: '2026-05-25', w: 85 }, { d: '2026-06-01', w: 85 }, { d: '2026-06-09', w: 85 },
      { d: '2026-06-16', w: 90 }, { d: '2026-06-23', w: 90 }, { d: '2026-07-01', w: 90 },
      { d: '2026-07-07', w: 90 }
    ],
    first: 70, last: 90, sessions: 10, weeks: 9
  },
  recentResults: [
    { initial: 'J', exercise: 'Bulgarian Split Squat (Dumbbells)', from: 10, to: 14, weeks: 12, daysAgo: 1 },
    { initial: 'C', exercise: 'Chest Press Machine', from: 15, to: 20, weeks: 11, daysAgo: 2 },
    { initial: 'P', exercise: 'Pec Deck Machine', from: 80, to: 92.5, weeks: 9, daysAgo: 2 },
    { initial: 'C', exercise: 'Cable Lateral Raise', from: 12.5, to: 17.5, weeks: 12, daysAgo: 2 },
    { initial: 'A', exercise: 'Smith Machine Good Morning', from: 15, to: 30, weeks: 13, daysAgo: 3 },
    { initial: 'H', exercise: 'Dumbbell Lunges', from: 7.5, to: 10, weeks: 8, daysAgo: 10 },
    { initial: 'W', exercise: 'Preacher Curl (Unilateral)', from: 6, to: 13.25, weeks: 22, daysAgo: 13 },
    { initial: 'C', exercise: 'Kneeling Cable Crunch', from: 25, to: 30, weeks: 11, daysAgo: 13 },
    { initial: 'B', exercise: 'Dumbbell Row (Unilateral)', from: 20, to: 24, weeks: 24, daysAgo: 16 },
    { initial: 'M', exercise: 'Power Shrug', from: 100, to: 140, weeks: 26, daysAgo: 16 },
    { initial: 'B', exercise: 'T-Bar Row Machine', from: 25, to: 30, weeks: 9, daysAgo: 17 },
    { initial: 'H', exercise: 'Bulgarian Split Squat (Dumbbells)', from: 5, to: 10, weeks: 13, daysAgo: 19 }
  ]
};

const nfNL = new Intl.NumberFormat('nl-NL');
const fmtKg = (v) => String(v).replace('.', ',');
const fmtMiljoenKg = (kg) =>
  `${(kg / 1e6).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} miljoen kg`;
const fmtDaysAgo = (days) => (days <= 0 ? 'vandaag' : days === 1 ? 'gisteren' : `${days} dagen geleden`);
const fmtSpan = (weeks) => {
  if (!weeks) return null;
  if (weeks <= 26) return `in ${weeks} weken`;
  return `in ${Math.round(weeks / 4.345)} maanden`;
};
const fmtLastReviewed = (iso) => {
  if (!iso) return null;
  return fmtDaysAgo(Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};

// Echte progressie-grafiek van één (geanonimiseerde) klant, rechtstreeks uit de app.
function ProgressChart({ showcase }) {
  if (!showcase || !showcase.series || showcase.series.length < 2) return null;
  const s = showcase.series;
  const W = 232, H = 80, PAD = 8;
  const min = Math.min(...s.map((p) => p.w));
  const max = Math.max(...s.map((p) => p.w));
  const x = (i) => PAD + (i / (s.length - 1)) * (W - PAD * 2);
  const y = (w) => H - PAD - ((w - min) / (max - min || 1)) * (H - PAD * 2);
  const points = s.map((p, i) => `${x(i)},${y(p.w)}`).join(' ');
  const area = `${PAD},${H} ${points} ${W - PAD},${H}`;
  return (
    <div className="oc-chart-card">
      <div className="oc-chart-head">
        <span className="oc-chart-title">{showcase.exercise} · echte klant</span>
        <span className="oc-chart-badge">uit de app</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
        aria-label={`Progressie van een klant op ${showcase.exercise}: van ${showcase.first} naar ${showcase.last} kg in ${showcase.weeks} weken`}>
        <defs>
          <linearGradient id="oc-chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,36,52,.28)" />
            <stop offset="100%" stopColor="rgba(255,36,52,0)" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#oc-chart-fill)" />
        <polyline points={points} fill="none" stroke="#ff2434" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {s.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.w)} r={i === s.length - 1 ? 3.5 : 2}
            fill={i === s.length - 1 ? '#fff' : '#ff2434'} />
        ))}
      </svg>
      <div className="oc-chart-foot">
        <span>{fmtKg(showcase.first)} kg</span>
        <span className="oc-chart-gain">+{fmtKg(showcase.last - showcase.first)} kg in {showcase.weeks} weken</span>
        <span>{fmtKg(showcase.last)} kg</span>
      </div>
    </div>
  );
}

// Curated: male transformations only (matches the 21-45 male ad targeting).
// NB voor Stef/Mike: laat hier alleen foto's van ECHTE eigen klanten staan.
const resultImages = [
  '/img/noindex/strongr-ba-1.jpg',
  '/img/noindex/strongr-ba-3.jpg',
  '/img/noindex/strongr-ba-2.jpg',
  '/img/before-after/before-after-4-lp.jpg',
  '/img/noindex/strongr-ba-4.jpg',
  '/img/resultaten/april2024/resultaat3.jpg'
];

const painPoints = [
  'Je traint al maanden (of jaren), maar niemand ziet er iets van. Jij ook niet, hoe vaak je ook in de spiegel kijkt.',
  'Je loopt de gym in zonder plan en kiest je oefeningen op de gok.',
  'Je weet niet of je zwaarder moet, meer sets of iets heel anders, dus je blijft hetzelfde doen. En hetzelfde blijven is precies het probleem.',
  'Elke maand een nieuw schema van YouTube of ChatGPT, maar nooit iets dat écht op jou is gebouwd.'
];

const futureWeeks = [
  {
    week: 'Week 1',
    title: 'Rust in je hoofd',
    text: 'Je loopt de gym in en weet exact wat je gaat doen. Geen twijfel bij het rek, geen half afgemaakte trainingen. Trainen voelt voor het eerst als uitvoeren in plaats van uitzoeken.'
  },
  {
    week: 'Week 4',
    title: 'De eerste bewijzen',
    text: 'De gewichten in je app zijn zichtbaar omhoog gekropen. Je shirt zit strakker om je schouders. Jij ziet het als eerste, en dat is genoeg om nooit meer te willen stoppen.'
  },
  {
    week: 'Week 12',
    title: 'Anderen zien het',
    text: '"Ben jij aan het trainen?" Kleding die anders valt, en in de spiegel het bewijs dat je niet meer zomaar wat doet. Dit is het punt waarop het geen poging meer is, maar een leefstijl.'
  }
];

// "Zo gaat het na je aanmelding": wat er gebeurt en wat de klant zelf doet.
// 24 uur = dezelfde belofte als de bevestigingsmail na de intake.
// "Wat krijg je?"-blok onder de hero. Bewust simpel: korte zinnen, geen
// vaktermen. Geen belofte over uitvoering of techniek (Stef kijkt naar wat je
// invult, niet naar hoe je de oefening doet).
const whatYouGet = [
  {
    title: 'Een trainingsschema dat Stef voor jou maakt',
    text: 'Je vult een paar vragen in. Stef maakt je schema. Dat is gratis.'
  },
  {
    title: 'Je weet elke training wat je moet doen',
    text: 'Je schema staat in een app op je telefoon. Daar zie je welke oefening je doet, hoe vaak en met welk gewicht.'
  },
  {
    title: 'Bij elke oefening zie je hoe het moet',
    text: 'Met een video of afbeelding. De video’s heeft Stef zelf opgenomen.'
  },
  {
    title: '14 dagen hulp van Stef via WhatsApp',
    text: 'Ook gratis. Stef kijkt na elke training zelf wat je hebt gedaan en zet de volgende met de hand voor je klaar. Je kunt hem alles vragen.'
  }
];

const afterSignupSteps = [
  {
    when: 'Nu',
    title: 'Korte intake',
    text: 'Drie minuten: je doel, hoe vaak je kunt trainen en of je in de sportschool of thuis traint.'
  },
  {
    when: 'Binnen 24 uur',
    title: 'Je schema staat klaar',
    text: 'Je krijgt een mail en een WhatsApp van Stef met een link. Tik erop, maak een account aan en je schema staat in de app.'
  },
  {
    when: 'Je eerste training',
    title: 'Jij vult je gewichten in',
    text: 'De oefeningen, sets en rusttijden staan al klaar. De eerste keer kies je zelf je gewicht.'
  },
  {
    when: 'Daarna',
    title: 'Stef zet alles klaar',
    text: 'Voor elke volgende training staan gewicht, herhalingen en rust klaar. Jij volgt. 14 dagen gratis, geen betaalgegevens.'
  }
];

const systemSteps = [
  {
    title: 'Vul de korte intake in',
    // 3 min = het echte pad: 8 schermen in de gym, 9-10 thuis (getStepPath in de
    // wizard). Zelfde getal als in de succes-modal - twee claims die elkaar op één
    // pagina tegenspreken kosten meer dan ze opleveren.
    text: 'Drie minuten: je doel, ervaring, hoe vaak je kunt trainen en welk materiaal je hebt.'
  },
  {
    title: 'Stef bouwt jouw schema',
    text: 'Persoonlijk trainingsschema in de app, afgestemd op jouw lichaam, niveau en agenda.'
  },
  {
    title: 'Jij traint, Stef kijkt na',
    text: 'Je logt je training in de app. Stef bekijkt elke sessie: elke set, elke herhaling, elk gewicht.'
  },
  {
    title: 'Je volgende sessie staat exact klaar',
    text: 'Gebaseerd op jouw vorige training: iedere week nét iets meer. Progressie als systeem, niet als geluk.'
  }
];

// Getranscribeerd uit echte reviews (screenshots in /img/recensies/, /img/hlp-fb-reacties.jpg, /img/recensie-whatsapp1.jpeg)
const reviews = [
  {
    name: 'Art',
    source: 'via Facebook',
    color: '#16a34a',
    text: 'Ik wil Stef bedanken voor alle hulp tijdens de afgelopen weken. Zoveel geleerd waar ik echt wat aan heb en aan zal blijven hebben. Ik ben weer blij wanneer ik in de spiegel kijk. Bedankt!'
  },
  {
    name: 'Lorenzo Scheffer',
    source: 'via Google',
    color: '#0ea5e9',
    text: 'Ik heb ontzettend lang geprobeerd aan te komen, maar niets dat ik zelf probeerde lukte. Stef heeft ontzettend veel kennis en weet dat goed over te brengen. Na 1 week was ik al 2,6 kg aangekomen - ik was heel verbaasd. Na het 8-wekentraject ben ik ruim 6 kg aangekomen. Ik heb zelf 2 mensen aangedragen die nu ook coaching van Stef krijgen. Ik zou het iedereen aanraden!'
  },
  {
    name: 'Mike Peeters',
    source: 'via Google',
    color: '#d97706',
    text: 'Dankzij Stef ben ik er eindelijk achter gekomen hoe ik gespierder kan worden. Ik dacht dat ik gewoon niet zwaarder kon worden en dat dit bij mij hoorde, maar Stef heeft mij in een aantal weken zoveel geleerd over mijn lichaam, dat ik nu eindelijk al weken achter elkaar zwaarder word. Ook word ik eindelijk sterker, wat mij hiervoor nooit lukte.'
  }
];

const moreReviews = [
  {
    name: 'Alexander Visser',
    source: 'via Google',
    color: '#7c3aed',
    text: 'Sinds 1 jaar eindelijk mezelf in de sportschool gekregen om serieus aan mezelf te werken. Eerst op eigen kracht, alleen merkte ik dat het aankomen in spieren met ups en downs ging. Sinds ik hulp van Stef krijg, merk ik dat mijn spieren stabiliseren en groeien. De laatste maanden kom ik wekelijks aan in spiermassa. 💪'
  },
  {
    name: 'Siradeghyan',
    source: 'via Google',
    color: '#dc2626',
    text: 'Prima coaching! Ik had altijd moeite met aankomen omdat ik 5 dagen hard trainde maar heel weinig calorieën at. Binnen 10 weken ben ik van 60 kg naar 69 kg gegaan - het zwaarste wat ik ooit woog, en best snel ook. Nu ga ik zeker nog door! Good job Stef! 💪'
  },
  {
    name: 'Klant via WhatsApp',
    source: 'appje aan Stef',
    color: '#25d366',
    text: 'Hee man, we zijn nu ongeveer 5 weken geleden begonnen en ben gewoon al 5 kg aangekomen! Ik ben enorm blij met hoe je me helpt tot nu toe. De aanpak is echt anders dan ik gewend ben en je hebt echt heel veel kennis.'
  },
  {
    name: 'Joost',
    source: 'via Facebook',
    color: '#2563eb',
    text: 'Aanvankelijk sceptisch over wat voor resultaten ik kon verwachten, maar positief verrast. Zeker mijn geld waard.'
  },
  {
    name: 'Dion',
    source: 'via Facebook',
    color: '#7c3aed',
    text: 'Altijd gedacht dat ik geen spieren kon opbouwen door een laag testosteronniveau. Stef heeft mij laten zien en bewezen dat dit niet waar was. Ik ben in 60 dagen meer spieren aangekomen dan in de afgelopen jaren, omdat het in mijn eentje gewoon niet lukte.'
  },
  {
    name: 'Frank',
    source: 'via Facebook',
    color: '#0ea5e9',
    text: 'Na meer dan 10 jaar niet gesport te hebben wist ik niet waar ik moest beginnen. Met het op maat gemaakte schema kon ik meteen aan de slag en met alle educatieve tools én de coaching van Stef was ik binnen no-time weer goed op weg. Bedankt Stef 🎉'
  }
];

const faqItems = [
  {
    q: 'Wat kost het na de gratis periode?',
    a: '€39 per maand, maandelijks opzegbaar. Je betaalt makkelijk met iDEAL. En ben je na je eerste betaalde maand niet tevreden? Dan krijg je je geld terug. Één mailtje is genoeg.'
  },
  {
    q: 'Moet ik betaalgegevens invullen om te starten?',
    a: 'Nee. Je start je 14 dagen gratis zonder betaalgegevens: geen iDEAL, geen kaart, niks. Wil je daarna niet verder? Dan hoef je niets te doen; er wordt nooit iets automatisch afgeschreven.'
  },
  {
    q: 'Kijkt er écht een coach naar mijn trainingen?',
    a: 'Ja. Elke training die je logt wordt persoonlijk door Stef nagekeken. Op basis daarvan zet hij je volgende sessie klaar: welke oefeningen, hoeveel sets en herhalingen, welk gewicht en hoeveel rust. Geen AI-trucje, geen standaard schema.'
  },
  {
    q: 'Ik ben beginner. Is dit iets voor mij?',
    a: 'Juist. Als beginner boek je met de juiste sturing de snelste progressie van iedereen. Je schema wordt volledig op jouw niveau gemaakt en groeit met je mee.'
  },
  {
    q: 'Ik train al jaren maar zie geen verschil meer. Helpt dit dan?',
    a: 'Dit is precies waarvoor het systeem gemaakt is. Stilstand komt bijna altijd door te weinig structurele progressie-druk. Doordat Stef elke sessie nakijkt en je gewichten stuurt, doorbreek je dat plateau systematisch.'
  },
  {
    q: 'Hoe vaak en hoe lang moet ik trainen?',
    a: 'Dat bepaal jij. In de korte intake geef je aan hoe vaak je kunt trainen (2 tot 6+ keer per week), waar (gym of thuis) en hoeveel tijd je hebt. Je schema wordt daarop gebouwd, niet andersom.'
  },
  {
    q: 'Wat als ik wil stoppen?',
    a: 'Opzeggen kan elke maand, direct vanuit de app, zonder belletje of opzegtermijn-trucs.'
  }
];

// Er staan drie EmailForm's op de pagina (hero, offer, final), elk met eigen
// state. Deze vlag staat bewust op module-niveau: hij blokkeert een tweede
// verzending ook als die uit een ánder formulier komt.
let leadSendInFlight = false;

function EmailForm({ onEmailCaptured, buttonLabel = 'Start 14 dagen gratis', location }) {
  const [email, setEmail] = useState('');
  const [hasTrackedTyping, setHasTrackedTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tikfout in het adres ("gmail.comi", "hotmail.con")? Dan eerst vragen of
  // het verbeterde adres bedoeld is: zo'n 3% van de leads ging hierdoor verloren.
  const [suggestion, setSuggestion] = useState(null);
  const [ignoredTypoFor, setIgnoredTypoFor] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fix = suggestEmailFix(email);
    if (fix && ignoredTypoFor !== email) {
      setSuggestion(fix);
      return;
    }
    submitEmail(email);
  };

  // eslint-disable-next-line no-shadow
  const submitEmail = async (email) => {
    setSuggestion(null);

    // Er loopt al een verzending → alleen de succes-stap tonen. Zonder deze
    // check ging een dubbeltik (of Enter + knop) twee keer door de guards heen,
    // omdat die pas ná de fetch gezet werden: 2x lead in Make én in de pixel.
    if (leadSendInFlight) {
      onEmailCaptured({ email });
      return;
    }

    // Dit device liet hier al eens een e-mailadres achter → apart event, zodat
    // het dashboard en de Meta pixel dezelfde persoon niet dubbel tellen.
    const isRepeatLead = hasSentLead('online_coaching');
    trackLp(isRepeatLead ? 'email_submit_repeat' : 'email_submit', { location });

    const maySend = canSendEmailCapture(email);
    if (maySend && !isDev()) {
      // Guards en pixel meteen afvuren, vóór de await. Alles wat pas ná de
      // netwerk-round-trip gebeurt laat een venster van seconden open waarin
      // een tweede submit dezelfde lead nog een keer wegschrijft.
      leadSendInFlight = true;
      setIsSubmitting(true);
      markEmailCaptureSent(email);
      markLeadSent('online_coaching');
      // Sinds 29-09-2026 is het e-mailadres het Lead-event (Meta optimaliseert
      // hierop): afgeronde intakes (~1 per dag) waren te weinig signaal om van
      // te leren. De afgeronde intake heet in floawch nu CompleteRegistration.
      // EmailCaptured blijft voor de continuïteit in Events Manager.
      if (window.fbq && !isRepeatLead) {
        window.fbq('track', 'Lead', {
          content_name: 'Email online coaching trial',
          content_category: 'lp_email'
        });
        window.fbq('trackCustom', 'EmailCaptured', {
          content_name: 'Email online coaching trial'
        });
      }
      try {
        await fetch('https://hook.eu2.make.com/nn5x6vho9okdr4r4wezdrvldn3huxmnm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userData: { email, name: '', last_name: '', phone: '', whatsappConsent: false },
            quizResults: null,
            aiSummary: null,
            event: 'email_capture_online_coaching',
            location,
            timestamp: new Date().toISOString()
          })
        });
      } catch (err) {
        console.error('Email capture failed', err);
      } finally {
        leadSendInFlight = false;
        setIsSubmitting(false);
      }
    }

    onEmailCaptured({ email });
  };

  return (
    <>
    {suggestion && (
      <div className="oc-typo" role="alert">
        <p>Bedoel je <strong>{suggestion}</strong>?</p>
        <div className="oc-typo-actions">
          <button
            type="button"
            className="oc-typo-yes"
            onClick={() => { setEmail(suggestion); submitEmail(suggestion); }}
          >
            Ja, verbeter
          </button>
          <button
            type="button"
            className="oc-typo-no"
            onClick={() => { setIgnoredTypoFor(email); submitEmail(email); }}
          >
            Nee, het klopt zo
          </button>
        </div>
      </div>
    )}
    <form className="oc-form" onSubmit={handleSubmit}>
      <input
        type="email"
        name="email"
        placeholder="Jouw e-mailadres"
        value={email}
        required
        autoComplete="email"
        inputMode="email"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
        enterKeyHint="go"
        onChange={(event) => {
          setEmail(event.target.value);
          if (suggestion) setSuggestion(null);
          if (!hasTrackedTyping && event.target.value.length > 0) {
            setHasTrackedTyping(true);
            trackLp('email_typing_started', { location });
            trackEvent('https://hook.eu2.make.com/tl1sygqimvdpw4jqpw9uizhzj3k3ucpa', {
              event: 'email_typing_started',
              page: 'online_coaching',
              location,
              timestamp: new Date().toISOString()
            });
          }
        }}
      />
      <button type="submit" disabled={isSubmitting}>{buttonLabel}</button>
    </form>
    </>
  );
}

function OnlineCoaching() {
  // Eigen paginatitel (de globale title is voor de hele site)
  useEffect(() => {
    const prev = document.title;
    document.title = 'Online coaching: 14 dagen gratis | STRONGR by Stef';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (window.fbq && !isDev()) {
      window.fbq('track', 'PageView');
    }
    trackEvent('https://hook.eu2.make.com/tl1sygqimvdpw4jqpw9uizhzj3k3ucpa', {
      event: 'page_view',
      page: 'online_coaching',
      timestamp: new Date().toISOString()
    });
  }, []);

  // Eigen tracking (page view, scroll-diepte, secties, exit) + noindex
  useEffect(() => {
    const cleanupNoIndex = applyNoIndex();
    const cleanupTracking = initLpTracking('online_coaching');
    return () => {
      cleanupTracking();
      cleanupNoIndex();
    };
  }, []);

  // Reveal-on-scroll
  const rootRef = useRef(null);
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('.oc-reveal');
    if (!els?.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('oc-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [showSuccess, setShowSuccess] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState('');

  // Live app-data; valt terug op FALLBACK_STATS als de fetch mislukt of leeg is
  const [stats, setStats] = useState(FALLBACK_STATS);
  useEffect(() => {
    fetch(STATS_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.totals) return;
        setStats((prev) => ({
          ...prev,
          ...data,
          progression: data.progression || prev.progression,
          showcase: data.showcase && data.showcase.series?.length >= 2 ? data.showcase : prev.showcase,
          recentResults: data.recentResults?.length ? data.recentResults : prev.recentResults
        }));
      })
      .catch(() => {});
  }, []);
  const [openFaq, setOpenFaq] = useState(null);
  const [showAllReviews, setShowAllReviews] = useState(false);

  // Sticky CTA pas tonen voorbij de hero - anders 2-3 rode knoppen boven de fold
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleEmailCaptured = ({ email }) => {
    setCapturedEmail(email || '');
    setShowSuccess(true);
  };

  const intakeUrl = buildFloawchUrl('/workouts', { email: capturedEmail || '', flow: 'trial' });

  const handleIntakeClick = () => {
    trackLp('intake_click');
    trackEvent('https://hook.eu2.make.com/qpg0nbidrsplewj1tccxmgcaso36xabx', {
      event: 'click_intake_button',
      email: capturedEmail || '',
      page: 'online_coaching',
      timestamp: new Date().toISOString()
    });
    setShowSuccess(false);
  };

  const scrollToHero = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const input = document.querySelector('.oc-hero input[type="email"]');
    if (input) setTimeout(() => input.focus(), 650);
  };

  return (
    <div className="oc-page" ref={rootRef}>

      {/* TOPBAR */}
      <div className="oc-topbar">
        <img src="/img/strongr-by-stef-logo-transparent.svg" alt="STRONGR by Stef" style={{ height: 44 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <span className="oc-trust" style={{ margin: 0, display: 'none' }} />
          <span style={{ color: '#b5ada6', fontSize: 14 }} className="hidden sm:inline">
            <span style={{ color: '#ffb800' }}>★★★★★</span>&nbsp; 5.0 op Google
          </span>
          <button
            onClick={() => { trackLp('cta_click', { location: 'topbar' }); scrollToHero(); }}
            className="oc-topbar-cta"
            style={{
              background: 'linear-gradient(180deg, #e51423, #b40a16)', color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 10, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', fontSize: 14
            }}
          >
            Start gratis
          </button>
        </div>
      </div>

      {/* HERO - bewust compact: op mobiel (95% van verkeer) moeten kop, subregel
          én e-mailknop boven de fold staan. Social proof i.p.v. generieke badge. */}
      <section className="oc-hero">
        <div className="oc-hero-inner">
          <div className="oc-hero-stars">
            <span className="oc-stars">★★★★★</span> 5.0 op Google · 36+ reviews
          </div>
          <h1>
            Zichtbaar <span className="oc-red">gespierder.</span>
          </h1>
          <p className="oc-sub">
            Loop de sportschool in en denk nergens meer over na.
            Stef zet alles voor je klaar.
          </p>
          <p className="oc-offer-line">
            Gratis trainingsschema op maat + 14 dagen hulp van Stef via WhatsApp.
          </p>
          <EmailForm onEmailCaptured={handleEmailCaptured} location="hero" buttonLabel="Ontvang je gratis schema" />
          <p className="oc-microcopy">
            <span>14 dagen gratis</span> · <span>Geen betaalgegevens</span> · <span>Stop wanneer je wilt</span>
          </p>
        </div>

        {/* Result marquee */}
        <div className="oc-marquee oc-reveal">
          <div className="oc-marquee-track">
            {[...resultImages, ...resultImages].map((src, i) => (
              <img key={i} src={src} alt="Transformatie van een klant" loading="lazy" decoding="async" />
            ))}
          </div>
        </div>

        {/* Proof bar - echte totalen uit de app */}
        <div className="oc-proofbar oc-reveal">
          <div className="oc-proofbar-item">
            <strong>{nfNL.format(stats.totals.sessionsLogged)}</strong>
            <span>trainingen gelogd in de app</span>
          </div>
          <div className="oc-proofbar-item">
            {/* Bewust gelijk aan gelogd: elke training wordt nagekeken, dus 1-op-1 */}
            <strong>{nfNL.format(stats.totals.sessionsLogged)}</strong>
            <span>persoonlijk nagekeken door Stef</span>
          </div>
          <div className="oc-proofbar-item">
            <strong>{fmtMiljoenKg(stats.totals.totalVolumeKg)}</strong>
            <span>totaal getild door klanten</span>
          </div>
          {fmtLastReviewed(stats.lastReviewedAt) && (
            <div className="oc-proofbar-item">
              <strong className="oc-proofbar-live">
                <span className="oc-live-dot" />{fmtLastReviewed(stats.lastReviewedAt)}
              </strong>
              <span>laatste training nagekeken</span>
            </div>
          )}
        </div>
      </section>

      {/* WAT KRIJG JE - het aanbod in gewone taal, direct onder de hero. Wie zich
          afvraagt "wat krijg ik nou precies?" moet het hier in tien seconden
          snappen, ook met een laag leesniveau: korte zinnen, geen vaktermen,
          en meteen wat er na de 14 dagen gebeurt. */}
      <section className="oc-section" data-lp-section="wat_krijg_je" style={{ paddingTop: 48, paddingBottom: 16 }}>
        <div className="oc-section-inner" style={{ maxWidth: 720 }}>
          <div className="oc-reveal" style={{ textAlign: 'center' }}>
            <p className="oc-kicker">Kort en duidelijk</p>
            <h2 className="oc-h2">Wat krijg je?</h2>
          </div>
          <div className="oc-card oc-reveal" style={{ marginTop: 24 }}>
            <ul className="oc-check-list">
              {whatYouGet.map((item, i) => (
                <li key={i}>
                  <Check size={22} style={{ color: '#ff2434', flexShrink: 0, marginTop: 2 }} />
                  <span>
                    <strong style={{ color: '#fff' }}>{item.title}</strong>
                    <br />
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
            <div style={{ borderTop: '1px solid rgba(255,255,255,.1)', marginTop: 22, paddingTop: 20 }}>
              <p style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>En na die 14 dagen?</p>
              <p style={{ color: '#c9c2bc', fontSize: 16, lineHeight: 1.6 }}>
                Dan stopt het vanzelf. Je vult geen betaalgegevens in, dus er wordt niets afgeschreven.
                Wil je door met Stef? Dan kost het €39 per maand. Je kunt elke maand stoppen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NA JE AANMELDING - verwachtingen zetten. De grootste uitval zit na de
          aanvraag (account aanmaken, eerste training), dus hier staat precies
          wat er gebeurt en wat je zelf moet doen. */}
      <section className="oc-section" data-lp-section="na_aanmelding" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="oc-section-inner">
          <div className="oc-reveal" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <p className="oc-kicker">Zo gaat het na je aanmelding</p>
            <h2 className="oc-h2">Van intake naar je eerste training</h2>
          </div>
          <div className="oc-steps">
            {afterSignupSteps.map((s, i) => (
              <div key={i} className="oc-card oc-step oc-reveal">
                <span className="oc-step-num">{i + 1}</span>
                <p style={{ color: '#ff2434', fontWeight: 700, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>{s.when}</p>
                <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ color: '#c9c2bc', fontSize: 15, lineHeight: 1.55 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="oc-section" data-lp-section="herkenning">
        <div className="oc-section-inner" style={{ maxWidth: 860 }}>
          <div className="oc-reveal" style={{ textAlign: 'center' }}>
            <p className="oc-kicker">Eerlijk is eerlijk</p>
            <h2 className="oc-h2">Herken je dit?</h2>
          </div>
          <div className="oc-pain-grid">
            {painPoints.map((item, i) => (
              <div key={i} className="oc-card oc-reveal" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <X style={{ color: '#ff2434', flexShrink: 0, marginTop: 2 }} size={22} />
                <span style={{ fontSize: 16.5, lineHeight: 1.55, color: '#e8e2dc' }}>{item}</span>
              </div>
            ))}
          </div>
          <p className="oc-lead oc-reveal" style={{ textAlign: 'center', marginTop: 36, fontStyle: 'italic' }}>
            Het ligt niet aan je inzet. Het ontbreekt je aan sturing: iemand die precies vertelt
            wat jouw volgende stap is, elke training opnieuw.
          </p>
        </div>
      </section>

      {/* FUTURE PACING */}
      <section className="oc-section" data-lp-section="future_pacing" style={{ background: 'rgba(255,255,255,.02)', borderTop: '1px solid rgba(255,255,255,.05)', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
        <div className="oc-section-inner">
          <div className="oc-reveal" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <p className="oc-kicker">Stel je voor</p>
            <h2 className="oc-h2">Zo ziet het eruit als het wél werkt</h2>
            <p className="oc-lead">
              Geen hype, gewoon wat er gebeurt als iemand elke training voor je uitdenkt en jij blijft komen.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginTop: 40 }}>
            {futureWeeks.map((w, i) => (
              <div key={i} className={`oc-card oc-reveal ${i === 2 ? 'oc-card-red' : ''}`}>
                <div style={{ color: '#ff2434', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', fontSize: 13, marginBottom: 10 }}>{w.week}</div>
                <h3 style={{ fontWeight: 700, fontSize: 19, marginBottom: 10 }}>{w.title}</h3>
                <p style={{ color: '#c9c2bc', fontSize: 15.5, lineHeight: 1.6 }}>{w.text}</p>
              </div>
            ))}
          </div>
          {stats.progression && (
            <div className="oc-datapoint oc-reveal">
              <span className="oc-datapoint-num">+{stats.progression.medianPct}%</span>
              <p>
                zoveel gaan klanten gemiddeld omhoog in gewicht op een oefening die ze 8+ weken
                trainen. Gemeten in de app zelf, geen belofte uit een advertentie.
              </p>
            </div>
          )}
          <div className="oc-reveal" style={{ textAlign: 'center', marginTop: 36 }}>
            <button onClick={() => { trackLp('cta_click', { location: 'future_section' }); scrollToHero(); }} style={{ background: 'linear-gradient(180deg, #e51423, #b40a16)', color: '#fff', border: 'none', padding: '15px 32px', borderRadius: 12, fontWeight: 700, fontSize: 16, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 8px 30px rgba(204,12,25,.35)' }}>
              Start vandaag, straks ben je blij dat je het deed
            </button>
          </div>
        </div>
      </section>

      {/* RESULTATEN */}
      <section className="oc-section" data-lp-section="resultaten">
        <div className="oc-section-inner">
          <div className="oc-reveal" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <p className="oc-kicker">Bewijs</p>
            <h2 className="oc-h2">Mannen die stopten met gokken</h2>
            <p className="oc-lead">
              Gewone mannen met een baan en een leven, geen fitnessmodellen. Het verschil: een
              coach die elke training voor ze uitdenkt.
            </p>
          </div>

          {/* Recente resultaten - anonieme PR's, live uit de app */}
          {stats.recentResults?.length > 0 && (
            <div className="oc-reveal">
              <div className="oc-results-marquee">
                <div className="oc-results-track">
                  {[...stats.recentResults, ...stats.recentResults].map((r, i) => (
                    <div key={i} className="oc-result-card">
                      <div className="oc-result-top">
                        <span className="oc-result-name">{r.initial}•••</span>
                        <span className="oc-result-when">{fmtDaysAgo(r.daysAgo)}</span>
                      </div>
                      <span className="oc-result-ex">{r.exercise}</span>
                      <span className="oc-result-gain">
                        {fmtKg(r.from)} kg → <strong>{fmtKg(r.to)} kg</strong>
                        {fmtSpan(r.weeks) && <span className="oc-result-span"> {fmtSpan(r.weeks)}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <p style={{ textAlign: 'center', color: '#8d857f', fontSize: 13.5, marginTop: 14 }}>
                Nieuwe persoonlijke records van klanten, rechtstreeks uit de app. Namen afgeschermd voor privacy.
              </p>
            </div>
          )}

          <div className="oc-gallery">
            {resultImages.slice(0, 6).map((src, i) => (
              <img key={i} src={src} alt={`Transformatie klant ${i + 1}`} loading="lazy" className="oc-reveal" />
            ))}
          </div>
        </div>
      </section>

      {/* HET SYSTEEM */}
      <section className="oc-section" data-lp-section="systeem" style={{ background: 'rgba(255,255,255,.02)', borderTop: '1px solid rgba(255,255,255,.05)', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
        <div className="oc-section-inner">
          <div className="oc-reveal" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <p className="oc-kicker">Zo werkt progressie wél</p>
            <h2 className="oc-h2">Progressie is geen toeval. Het is een systeem.</h2>
            <p className="oc-lead">
              Spiergroei komt niet van motivatie of het perfecte schema, maar van <strong style={{ color: '#fff' }}>progressieve
              overload</strong>: elke week nét iets meer dan de vorige. Zelf bijhouden wat "iets meer" is,
              is precies waar iedereen de mist in gaat. Daarom doet Stef dat voor je.
            </p>
          </div>
          <div className="oc-steps">
            {systemSteps.map((s, i) => (
              <div key={i} className={`oc-card oc-step oc-reveal ${i === 3 ? 'oc-card-red' : ''}`}>
                <span className="oc-step-num">{i + 1}</span>
                <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ color: '#c9c2bc', fontSize: 15, lineHeight: 1.55 }}>{s.text}</p>
              </div>
            ))}
          </div>
          <p className="oc-reveal" style={{ textAlign: 'center', marginTop: 36, fontSize: 19, fontWeight: 600 }}>
            Jij levert de inzet. Stef levert het plan. <span style={{ color: '#ff2434' }}>Elke sessie opnieuw.</span>
          </p>
        </div>
      </section>

      {/* DE APP */}
      <section className="oc-section" data-lp-section="app">
        <div className="oc-section-inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 60, alignItems: 'center' }}>
          <div className="oc-reveal" style={{ position: 'relative', padding: '30px 0' }}>
            <div className="oc-phone">
              <img src="/img/app/ui-app-sessie-lp.jpg" alt="De STRONGR app: sets, herhalingen, gewicht en rust staan exact klaar" loading="lazy" decoding="async" />
            </div>
            <div className="oc-chip" style={{ top: 40, right: 0 }}>
              <Check size={16} style={{ color: '#22c55e' }} /> Nagekeken door Stef
            </div>
            <ProgressChart showcase={stats.showcase} />
          </div>
          <div className="oc-reveal">
            <p className="oc-kicker">De app</p>
            <h2 className="oc-h2">Alles staat voor je klaar. Elke training weer.</h2>
            <ul style={{ marginTop: 24 }} className="oc-check-list">
              {[
                'Per oefening exact hoeveel sets, herhalingen en gewicht. Vooraf ingevuld door Stef.',
                'Loggen kost een paar tikken; Stef ziet direct wat je hebt gedaan.',
                'Je ziet je eigen gewichten week na week omhoog gaan.'
              ].map((item, i) => (
                <li key={i}>
                  <Check size={22} style={{ color: '#ff2434', flexShrink: 0, marginTop: 2 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="oc-section" data-lp-section="reviews">
        <div className="oc-section-inner">
          <div className="oc-reveal" style={{ textAlign: 'center' }}>
            <p className="oc-kicker">36+ vijfsterren-reviews</p>
            <h2 className="oc-h2">Wat klanten zeggen</h2>
          </div>
          <div className="oc-reviews">
            {[...reviews, ...(showAllReviews ? moreReviews : [])].map((r, i) => (
              <div key={i} className={`oc-card ${i < 3 ? 'oc-reveal' : ''}`}>
                <div className="oc-review-stars">★★★★★</div>
                <p className="oc-review-text">"{r.text}"</p>
                <div className="oc-review-author">
                  <span className="oc-avatar" style={{ background: r.color }}>{r.name.charAt(0)}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{r.name}</div>
                    <div style={{ color: '#8d857f', fontSize: 13 }}>{r.source}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 30 }}>
            <button
              onClick={() => {
                if (!showAllReviews) trackLp('reviews_toggle', { value: 1 });
                setShowAllReviews(!showAllReviews);
              }}
              style={{
                background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)',
                color: '#f5f2ef', fontWeight: 600, fontSize: 15, fontFamily: 'inherit',
                padding: '13px 28px', borderRadius: 12, cursor: 'pointer'
              }}
            >
              {showAllReviews ? 'Minder reviews tonen' : `Meer reviews (+${moreReviews.length})`}
            </button>
          </div>
        </div>
      </section>

      {/* JOUW COACH */}
      <section className="oc-section" data-lp-section="coach" style={{ background: 'rgba(255,255,255,.02)', borderTop: '1px solid rgba(255,255,255,.05)', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
        <div className="oc-section-inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 50, alignItems: 'center' }}>
          <div className="oc-reveal">
            <img
              src="/img/stef-schrijven-lp.jpg"
              alt="Coach Stef"
              loading="lazy"
              decoding="async"
              style={{ width: '100%', borderRadius: 20, border: '1px solid rgba(255,255,255,.1)', maxHeight: 480, objectFit: 'cover' }}
            />
          </div>
          <div className="oc-reveal">
            <p className="oc-kicker">Jouw coach</p>
            <h2 className="oc-h2">Eén coach. Geen callcenter.</h2>
            <p className="oc-lead" style={{ marginBottom: 18 }}>
              Elke training die jij logt komt bij één iemand terecht: <strong style={{ color: '#fff' }}>Stef</strong>.
              Hij bouwt je schema, kijkt je sessies na en stuurt je progressie bij. Geen wisselende
              "coaches", geen chatbot.
            </p>
            <p className="oc-lead" style={{ marginBottom: 18 }}>
              Juist omdat hij alles persoonlijk nakijkt, neemt hij een <strong style={{ color: '#fff' }}>beperkt
              aantal nieuwe klanten per maand</strong> aan. Kwaliteit gaat boven volume.
            </p>
            <p className="oc-lead">
              Vragen tijdens je proefperiode? Je bereikt hem direct via de app of WhatsApp.
            </p>
          </div>
        </div>
      </section>

      {/* AANBOD */}
      <section className="oc-section" id="offer" data-lp-section="aanbod">
        <div className="oc-section-inner">
          <div className="oc-reveal" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <p className="oc-kicker">Het aanbod</p>
            <h2 className="oc-h2">Probeer het 14 dagen. Gratis. Echt gratis.</h2>
            <p className="oc-lead">
              Een personal trainer in de gym kost je al snel €60 per sessie.
              Bij STRONGR krijg je een coach op élke training, voor €39 per maand. En de eerste twee weken betaal je niks.
            </p>
          </div>
          <div className="oc-offer-card oc-reveal">
            <div className="oc-offer-left">
              <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 22 }}>Wat je krijgt:</h3>
              <ul className="oc-check-list">
                {[
                  'Persoonlijk trainingsschema, gebouwd op jouw intake',
                  'Elke training persoonlijk nagekeken door Stef',
                  'Elke volgende sessie exact klaargezet: sets, reps, gewicht, rust',
                  'Direct contact met Stef via WhatsApp',
                  'Niet tevreden na je eerste betaalde maand? Geld terug.'
                ].map((item, i) => (
                  <li key={i}>
                    <Check size={22} style={{ color: '#ff2434', flexShrink: 0, marginTop: 2 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="oc-offer-right">
              <div style={{ color: '#ff8a93', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', fontSize: 13 }}>De eerste 14 dagen</div>
              <div className="oc-price-zero">€0</div>
              <div style={{ color: '#c9c2bc', marginTop: 8, marginBottom: 26 }}>daarna €39/mnd · maandelijks opzegbaar · iDEAL</div>
              <EmailForm onEmailCaptured={handleEmailCaptured} location="offer" buttonLabel="Start gratis" />
              <p style={{ color: '#8d857f', fontSize: 13, marginTop: 14 }}>
                Stef kijkt alles persoonlijk na. Daarom is er maandelijks plek voor een beperkt aantal nieuwe klanten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VOOR WIE (NIET) */}
      <section className="oc-section" data-lp-section="voor_wie" style={{ paddingTop: 0 }}>
        <div className="oc-section-inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
          <div className="oc-card oc-reveal">
            <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 20 }}>Dit is voor jou als je…</h3>
            <ul className="oc-check-list">
              {[
                'gespierder of sterker wilt worden (en er gewoon beter uit wilt zien)',
                'wilt trainen met een plan dat élke week met je meegroeit',
                '2 tot 6 keer per week kunt trainen, in de gym of thuis',
                'klaar bent met zelf uitzoeken en gewoon wilt uitvoeren'
              ].map((item, i) => (
                <li key={i}>
                  <Check size={22} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="oc-card oc-reveal">
            <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 20 }}>Dit is níét voor jou als je…</h3>
            <ul className="oc-check-list">
              {[
                'op zoek bent naar een afvalprogramma of dieet-app',
                'een wondermiddel verwacht zonder zelf te trainen',
                'liever losse schema’s blijft verzamelen dan er één echt te volgen'
              ].map((item, i) => (
                <li key={i}>
                  <X size={22} style={{ color: '#8d857f', flexShrink: 0, marginTop: 2 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="oc-section" data-lp-section="faq" style={{ paddingTop: 20 }}>
        <div className="oc-section-inner" style={{ maxWidth: 760 }}>
          <div className="oc-reveal" style={{ textAlign: 'center' }}>
            <p className="oc-kicker">Vragen</p>
            <h2 className="oc-h2">Veelgestelde vragen</h2>
          </div>
          <div style={{ marginTop: 36 }}>
            {faqItems.map((item, i) => (
              <div key={i} className="oc-faq-item oc-reveal">
                <button onClick={() => {
                  if (openFaq !== i) trackLp('faq_open', { value: i, meta: { q: item.q } });
                  setOpenFaq(openFaq === i ? null : i);
                }}>
                  <span>{item.q}</span>
                  <ChevronDown size={20} style={{ flexShrink: 0, transition: 'transform .25s ease', transform: openFaq === i ? 'rotate(180deg)' : 'none' }} />
                </button>
                {openFaq === i && <p>{item.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="oc-final" data-lp-section="final_cta">
        <div style={{ position: 'relative', maxWidth: 680, margin: '0 auto' }} className="oc-reveal">
          <h2 className="oc-h2">Over een paar maanden wilde je dat je vandaag was begonnen</h2>
          <p className="oc-lead" style={{ marginBottom: 34 }}>
            Start je 14 dagen gratis. Geen betaalgegevens, geen risico. Alleen een schema dat eindelijk voor jou is gemaakt.
          </p>
          <EmailForm onEmailCaptured={handleEmailCaptured} location="final" />
          <p className="oc-microcopy">
            <span>✓ Geen betaalgegevens nodig</span> &nbsp;·&nbsp; <span>✓ Stopt automatisch</span>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,.08)', padding: '60px 20px 0', background: '#100d0b' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 40, paddingBottom: 50 }}>
            <div>
              <img src="/img/strongr-by-stef-logo-transparent.svg" alt="STRONGR by Stef" style={{ height: 60, marginBottom: 16 }} />
              <p style={{ color: '#8d857f', fontSize: 14.5, lineHeight: 1.6, maxWidth: 280 }}>
                Persoonlijke online coaching voor mannen die gespierder en sterker willen worden.
                Elke training nagekeken, elke sessie uitgedacht.
              </p>
              <div style={{ marginTop: 14, color: '#b5ada6', fontSize: 14 }}>
                <span style={{ color: '#ffb800', letterSpacing: 2 }}>★★★★★</span>&nbsp; 36+ vijfsterren-reviews
              </div>
            </div>
            <div>
              <h4 style={{ fontWeight: 700, fontSize: 15, textTransform: 'uppercase', letterSpacing: '.08em', color: '#8d857f', marginBottom: 16 }}>Aan de slag</h4>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 15.5 }}>
                <li><button onClick={() => { trackLp('cta_click', { location: 'footer' }); scrollToHero(); }} style={{ background: 'none', border: 'none', color: '#e8e2dc', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}>Start 14 dagen gratis</button></li>
                <li><a href="#offer" style={{ color: '#e8e2dc', textDecoration: 'none' }}>Wat je krijgt</a></li>
              </ul>
              <p style={{ color: '#8d857f', fontSize: 13.5, marginTop: 18 }}>
                Geen betaalgegevens nodig · daarna €39/mnd<br />Betalen met iDEAL · maandelijks opzegbaar
              </p>
            </div>
            <div>
              <h4 style={{ fontWeight: 700, fontSize: 15, textTransform: 'uppercase', letterSpacing: '.08em', color: '#8d857f', marginBottom: 16 }}>Contact</h4>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 15.5 }}>
                <li><a href="mailto:info@healthyliving-project.com" style={{ color: '#e8e2dc', textDecoration: 'none' }}>info@healthyliving-project.com</a></li>
                <li><a href="https://www.instagram.com/stefridder.coaching/" target="_blank" rel="noreferrer" style={{ color: '#e8e2dc', textDecoration: 'none' }}>Instagram: @stefridder.coaching</a></li>
              </ul>
              <p style={{ color: '#8d857f', fontSize: 13.5, marginTop: 18 }}>
                Vragen? Stef antwoordt zelf, meestal dezelfde dag nog.
              </p>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,.07)', padding: '22px 0 90px', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', color: '#6d665f', fontSize: 13 }}>
            <span>© Healthy Living Project {new Date().getFullYear()}. Alle rechten voorbehouden.</span>
            <span>Resultaten verschillen per persoon en vragen consistente training.</span>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA (pas zichtbaar voorbij de hero) */}
      {showSticky && (
      <div className="oc-sticky">
        <button
          onClick={() => { trackLp('cta_click', { location: 'sticky' }); scrollToHero(); }}
          style={{
            width: '100%', border: 'none', borderRadius: 12, padding: '15px 0',
            background: 'linear-gradient(180deg, #e51423, #b40a16)', color: '#fff',
            fontWeight: 700, fontSize: 16, fontFamily: 'inherit', cursor: 'pointer'
          }}
        >
          Ontvang je gratis schema
        </button>
      </div>
      )}

      {/* Success modal */}
      {showSuccess && (
        <div className="oc-modal-overlay">
          <div className="oc-modal">
            <button
              onClick={() => setShowSuccess(false)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#8d857f' }}
            >
              <X size={20} />
            </button>
            <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Goede keus!</h3>
            {/* Gebouwd op de /goede-voornemens-copy, met twee toevoegingen: de intake
                wordt verantwoord (geen standaardschema) en de mailbox-fallback duwt niet
                meer richting uitstel. Sinds het Lead-event op intake-stap 1 ligt, telt
                iedereen die hier afhaakt niet meer mee - deze modal is dus het scharnier
                van de funnel geworden. */}
            <p style={{ color: '#c9c2bc', lineHeight: 1.6, marginBottom: 22 }}>
              Ik stuur geen standaardschema. Om er één te bouwen dat echt bij jóu past, moet ik
              weten wat je doel is, hoe vaak je kunt trainen en of je thuis of in de gym staat.
              Dat vraag ik in het korte intakeformulier. Duurt ongeveer 3 minuten.
              <br /><br />
              Doe het nu meteen, dan kun je bij je eerstvolgende training al met je eigen schema
              beginnen. Lukt het echt niet? De link staat ook in je mailbox. Check dan even je
              spam, ik stuur vanuit info@healthyliving-project.com.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a
                href={intakeUrl}
                onClick={handleIntakeClick}
                style={{
                  flex: 1, minWidth: 180, textAlign: 'center', textDecoration: 'none',
                  background: 'linear-gradient(180deg, #e51423, #b40a16)', color: '#fff',
                  fontWeight: 700, padding: '14px 20px', borderRadius: 12
                }}
              >
                Maak mijn schema op maat
              </a>
              <button
                onClick={() => setShowSuccess(false)}
                style={{
                  background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)',
                  color: '#c9c2bc', fontWeight: 600, padding: '14px 20px', borderRadius: 12,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnlineCoaching;
